import { FC, ReactNode, useMemo, useState } from 'react';
import { AlertBox, Button, Input, Loader, SvgIcon } from '@/components';
import { EMAIL_REGEX, navigationPages, COMMON_CONTAINER_ID } from '@/config';
import { Validation } from '@/models';
import {
  amlbotAPI,
  OtpAuthEmailRequest,
  OtpAuthEmailResponse,
  OtpAuthOtpRequest,
  OtpAuthResponse,
  UserPreferenceEnum,
} from '@/api';
import { useApiCall, useInputText } from '@/hooks';
import { parseError } from '@/redux/helpers';
import { useSearchParams } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { toast } from 'react-toastify';

import { Steps } from '@/components/Steps';

import styles from './OtpForm.module.sass';

export interface OtpFormProps {
  preference: UserPreferenceEnum;
  showCompanyName?: boolean;
  stepsLabels?: { email: string; verification: string };
  stepTitles?: { 1?: string; 2?: string };
  onBeforeVerify?: (email: string, companyName: string) => void;
  onSuccess?: (token: string) => void;
  footer?: ReactNode;
  traceHashFor?: string;
}

const OtpForm: FC<OtpFormProps> = ({
  preference,
  showCompanyName = false,
  stepsLabels,
  stepTitles,
  onBeforeVerify,
  onSuccess,
  footer,
  traceHashFor,
}) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [searchParams] = useSearchParams();

  const steps = [
    { slug: 'email', label: stepsLabels?.email ?? 'Email' },
    {
      slug: 'verification',
      label: stepsLabels?.verification ?? 'Verification',
    },
  ];

  const EMAIL_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: 'Field is required',
      },
      {
        name: 'email',
        value: EMAIL_REGEX,
        message: 'Invalid email address',
      },
      {
        name: 'maxLength',
        value: 200,
        message: `Max length is 200 chars`,
      },
    ],
    [],
  );

  const COMPANY_NAME_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: 'Field is required',
      },
    ],
    [],
  );

  const OTP_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: 'Field is required',
      },
      {
        name: 'maxLength',
        value: 6,
        message: `Max length is 6 chars`,
      },
    ],
    [],
  );

  const emailField = useInputText('', EMAIL_VALIDATIONS);
  const otpField = useInputText('', OTP_VALIDATIONS);
  const companyNameField = useInputText(
    '',
    showCompanyName ? COMPANY_NAME_VALIDATIONS : [],
  );

  const [submitEmailHandler, loadingEmail, errorEmail] = useApiCall(
    async () => {
      const isEmailOk = emailField.isValid;
      const isCompanyOk = !showCompanyName || companyNameField.isValid;

      if (!isEmailOk) emailField.onBlur();
      if (!isCompanyOk) companyNameField.onBlur();
      if (!isEmailOk || !isCompanyOk) return;

      setLoading(true);
      try {
        const payload: OtpAuthEmailRequest = {
          email: emailField.value as string,
          companyName: companyNameField.value as string,
        };

        try {
          const response: OtpAuthEmailResponse =
            await amlbotAPI.onboardOtpEmail(payload);

          if (response.success) {
            setStep(2);
            toast.success('OTP code sent to your email!', {
              containerId: COMMON_CONTAINER_ID,
            });
          }

          if (!response.success || errorEmail) {
            toast.error(errorEmail, { containerId: COMMON_CONTAINER_ID });
          }
        } catch (error) {
          const { message } = parseError(error);
          toast.error(message, { containerId: COMMON_CONTAINER_ID });
        }
      } finally {
        setLoading(false);
      }
    },
  );

  const [submitOtpHandler, loadingOtp, errorOtp] = useApiCall(async () => {
    if (!otpField.isValid) {
      otpField.onBlur();
      return;
    }

    setLoading(true);
    try {
      const payload: OtpAuthOtpRequest = {
        email: emailField.value as string,
        otp: otpField.value as string,
        ref: searchParams.get('ref') || '',
        preference,
        ...(traceHashFor ? { traceHashFor } : {}),
      };

      onBeforeVerify?.(
        emailField.value as string,
        companyNameField.value as string,
      );

      try {
        const response: OtpAuthResponse = await amlbotAPI.verifyOtp(payload);

        if (response.success) {
          toast.success('Successfully verified! Redirecting...', {
            containerId: COMMON_CONTAINER_ID,
          });

          if (onSuccess) {
            onSuccess(response.user?.emailAddress ?? '');
          } else {
            window.location.href = navigationPages.DASHBOARD.path;
          }
        }

        if (!response.success || !!errorOtp) {
          toast.error(errorOtp, { containerId: COMMON_CONTAINER_ID });
        }
      } catch (error) {
        const { message } = parseError(error);
        toast.error(message, { containerId: COMMON_CONTAINER_ID });
      }
    } finally {
      setLoading(false);
    }
  });

  const isLoading = loading || loadingEmail || loadingOtp;

  const defaultStepTitles: Record<number, string> = {
    1: 'Get started',
    2: 'Enter the OTP code we sent to your email to continue',
  };

  const formTitle =
    (step === 1 ? stepTitles?.[1] : stepTitles?.[2]) ?? defaultStepTitles[step];

  useHotkeys(
    'enter',
    (event) => {
      event.preventDefault();
      if (step === 1) submitEmailHandler();
      if (step === 2) submitOtpHandler();
    },
    { enableOnFormTags: true },
    [],
  );

  return (
    <div className={styles.form}>
      <h3 className={styles.formTitle}>{formTitle}</h3>

      <Steps
        currentStep={step}
        steps={steps}
        className={styles.steps}
        onClickStep={setStep}
      />

      <div className={styles.fields}>
        {step === 1 && (
          <div className={styles.step}>
            <div className={styles.field}>
              <Input
                id="email"
                name="email"
                type="text"
                value={emailField.value as string}
                onChange={emailField.onChange}
                onBlur={emailField.onBlur}
                placeholder="Email"
                disabled={isLoading}
                isError={emailField.isDirty && !emailField.isValid}
                required
                floating
              />
              {emailField.isDirty && !emailField.isValid && (
                <AlertBox type="danger" className={styles.error} small>
                  {{ description: emailField.errors[0] }}
                </AlertBox>
              )}
            </div>

            {showCompanyName && (
              <div className={styles.field}>
                <Input
                  id="company"
                  name="company"
                  type="text"
                  value={companyNameField.value as string}
                  onChange={companyNameField.onChange}
                  onBlur={companyNameField.onBlur}
                  placeholder="Organisation Name"
                  disabled={isLoading}
                  isError={
                    companyNameField.isDirty && !companyNameField.isValid
                  }
                  className={styles.companyNameInput}
                  required
                  floating
                />
                {companyNameField.isDirty && !companyNameField.isValid && (
                  <AlertBox type="danger" className={styles.error} small>
                    {{ description: companyNameField.errors[0] }}
                  </AlertBox>
                )}
              </div>
            )}

            <Button
              size="xl"
              corner="round"
              className={styles.submitButton}
              onClick={submitEmailHandler}
              disabled={isLoading}
              fullwidth
            >
              Get Verification Code
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className={styles.step}>
            <div className={styles.field}>
              <Input
                id="otp"
                name="otp"
                type="text"
                value={otpField.value as string}
                maxLength={6}
                onChange={otpField.onChange}
                onBlur={otpField.onBlur}
                placeholder="OTP code"
                disabled={isLoading}
                isError={otpField.isDirty && !otpField.isValid}
                required
                floating
              />
              {otpField.isDirty && !otpField.isValid && (
                <AlertBox type="danger" className={styles.error} small>
                  {{ description: otpField.errors[0] }}
                </AlertBox>
              )}
            </div>

            <Button
              size="xl"
              corner="round"
              className={styles.submitButton}
              onClick={submitOtpHandler}
              disabled={isLoading}
              fullwidth
            >
              Sign up
            </Button>

            <button
              type="button"
              className={styles.backLink}
              onClick={() => setStep(1)}
              disabled={isLoading}
            >
              <SvgIcon name="arrow-back" />
              Get back
            </button>
          </div>
        )}
      </div>

      {footer && <div className={styles.footer}>{footer}</div>}

      {isLoading && (
        <div className={styles.loader}>
          <Loader size="medium" />
        </div>
      )}
    </div>
  );
};

export default OtpForm;
